/** Condiciones comerciales del presupuesto (editables por presupuesto, aparecen en el PDF). */
import React, { useState, useEffect } from 'react';
import api from '@/api/axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, X, FloppyDisk, DownloadSimple } from '@phosphor-icons/react';

export const CondicionesCard = ({ proyecto, bloqueado, onSaved }) => {
  const [rows, setRows] = useState(proyecto.condiciones || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRows(proyecto.condiciones || []); }, [proyecto.id]);

  const setRow = (i, campo, val) => {
    setRows((prev) => {
      const arr = [...prev];
      arr[i] = { ...arr[i], [campo]: val };
      return arr;
    });
  };

  const cargarDefaults = async () => {
    try {
      const { data } = await api.get('/sa/empresa');
      const def = data.condiciones_default || [];
      if (!def.length) return toast.info('No hay condiciones predeterminadas configuradas (Administración → Empresa / PDF)');
      setRows(def.map((c) => ({ ...c })));
      toast.success('Condiciones predeterminadas cargadas — guarda para aplicarlas');
    } catch (e) { toast.error('No se pudieron cargar las predeterminadas'); }
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put(`/sa/proyectos/${proyecto.id}/condiciones`, { condiciones: rows.filter((r) => r.titulo || r.texto) });
      toast.success('Condiciones guardadas');
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudieron guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-4 space-y-2" data-testid="pre-condiciones-card">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Condiciones comerciales</h3>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={cargarDefaults} disabled={bloqueado} data-testid="cond-cargar-defaults" title="Cargar condiciones predeterminadas">
            <DownloadSimple size={13} className="mr-0.5" /> Predet.
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" disabled={bloqueado}
            onClick={() => setRows((prev) => [...prev, { titulo: '', texto: '' }])} data-testid="cond-add">
            <Plus size={13} />
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-[var(--text-muted)]">Vigencia, tiempo de entrega, forma de pago, anticipo, garantía… Aparecen al final del PDF.</p>
      {rows.length === 0 && <p className="text-xs text-[var(--text-muted)] italic">Sin condiciones — el PDF usará las predeterminadas si existen.</p>}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="space-y-1 border border-[var(--border)] rounded-lg p-2">
            <div className="flex gap-1.5 items-center">
              <Input value={r.titulo || ''} onChange={(e) => setRow(i, 'titulo', e.target.value)} placeholder="Título (ej. Vigencia)"
                disabled={bloqueado} className="h-8 text-xs" data-testid={`cond-titulo-${i}`} />
              <button type="button" className="text-red-400" disabled={bloqueado}
                onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))} data-testid={`cond-del-${i}`}>
                <X size={14} />
              </button>
            </div>
            <Textarea rows={2} value={r.texto || ''} onChange={(e) => setRow(i, 'texto', e.target.value)} placeholder="Texto"
              disabled={bloqueado} className="text-xs min-h-[40px]" data-testid={`cond-texto-${i}`} />
          </div>
        ))}
      </div>
      <Button size="sm" onClick={guardar} disabled={saving || bloqueado} className="w-full h-8 bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="cond-guardar">
        <FloppyDisk size={14} className="mr-1" /> {saving ? 'Guardando…' : 'Guardar condiciones'}
      </Button>
    </Card>
  );
};
